import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocalState, useOnline } from '@ndrc/app-sdk/client';
import { callCore, mutateCore } from '../lib/rpc';
import { useDesk } from '../desk/context';
import { t } from '../messages';
interface Validation {
  runId: string;
  version: number;
  activityTitle: string;
  activityStatus: string;
  members: string[];
  blockId: string;
  title: string;
  criteria: string[];
  commandStatus: string | null;
  commandError: string | null;
}
export function CommandState({
  classId,
  commandId,
  mode = 'validation',
}: {
  classId: string;
  commandId: string;
  mode?: 'validation' | 'delivery' | 'operation';
}) {
  const query = useQuery({
    queryKey: ['core', 'commands.status', classId, commandId],
    queryFn: () =>
      callCore<{ status: string; firstError: string | null; workerSilent: boolean }>(
        'commands.status',
        { classId, commandId },
      ),
    retry: false,
    refetchInterval: (query) =>
      ['pending', 'running'].includes(query.state.data?.status ?? 'pending') ? 2000 : false,
  });
  const labels = {
    pending: 'teacher.validation.pending',
    running:
      mode === 'operation'
        ? 'teacher.command.running'
        : mode === 'delivery'
          ? 'teacher.controlAction.running'
          : 'teacher.validation.running',
    ok:
      mode === 'operation'
        ? 'teacher.command.ok'
        : mode === 'delivery'
          ? 'teacher.controlAction.delivered'
          : 'teacher.validation.ok',
    failed:
      mode === 'operation'
        ? 'teacher.command.failed'
        : mode === 'delivery'
          ? 'teacher.controlAction.failed'
          : 'teacher.validation.failed',
    partial:
      mode === 'operation'
        ? 'teacher.command.failed'
        : mode === 'delivery'
          ? 'teacher.controlAction.failed'
          : 'teacher.validation.failed',
  } as const;
  const status = query.data?.status;
  return (
    <div role="status">
      <strong>{t('teacher.validation.latest')}</strong>
      <p>
        {status && Object.hasOwn(labels, status)
          ? t(labels[status as keyof typeof labels])
          : t('teacher.validation.unknown')}
      </p>
      {query.isError && <p role="alert">{query.error.message}</p>}
      {query.data?.firstError && <p>{query.data.firstError}</p>}
      {query.data?.workerSilent && <p>{t('teacher.validation.silent')}</p>}
    </div>
  );
}
export function MissionValidations({ classId }: { classId: string }) {
  const { me } = useDesk();
  const online = useOnline();
  const cache = useQueryClient();
  const [commandId, setCommandId] = useLocalState<string | null>(
    `mission-validation:${me.id}:${classId}`,
    null,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const query = useQuery({
    queryKey: ['core', 'missions.validations', classId],
    queryFn: () => callCore<{ validations: Validation[] }>('missions.validations', { classId }),
    retry: false,
    refetchInterval: 3000,
  });
  async function validate(row: Validation) {
    setBusy(true);
    setError('');
    try {
      const result = await mutateCore<{ commandId: string }>('missions.validate', {
        classId,
        runId: row.runId,
        blockId: row.blockId,
        expectedVersion: row.version,
      });
      setCommandId(result.commandId);
      await cache.invalidateQueries({ queryKey: ['core', 'missions.validations', classId] });
    } catch (e) {
      setError(e instanceof Error ? e.message : t('teacher.gates.error'));
    } finally {
      setBusy(false);
    }
  }
  if (!commandId && (query.isPending || (!query.isError && !query.data.validations.length)))
    return null;
  return (
    <section className="teacher-card">
      <h3>{t('teacher.validation.title')}</h3>
      {commandId && <CommandState classId={classId} commandId={commandId} />}
      {error && <p role="alert">{error}</p>}
      {query.isError && <p role="alert">{t('teacher.gates.error')}</p>}
      {query.data?.validations.map((row) => (
        <section key={row.runId}>
          <h4>
            {row.activityTitle} — {row.members.join(', ')}
          </h4>
          <h5>{row.title}</h5>
          <ul>
            {row.criteria.map((criterion, index) => (
              <li key={index}>{criterion}</li>
            ))}
          </ul>
          {row.commandError && <p role="alert">{row.commandError}</p>}
          <button
            type="button"
            className="ndrc-button"
            disabled={
              busy ||
              !online ||
              row.activityStatus !== 'running' ||
              ['pending', 'running'].includes(row.commandStatus ?? '')
            }
            onClick={() => {
              void validate(row);
            }}
          >
            {t('teacher.validation.submit')}
          </button>
        </section>
      ))}
    </section>
  );
}
